import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { API_ENDPOINTS } from '../../config/apiConfig.js';
import { TURNSTILE_SITE_KEY } from '../../config/env';
import TurnstileWidget from '../../components/website/TurnstileWidget';
import { useAuth } from '../../components/router/authContext.jsx';
import { useScrollAnimation } from '../../hooks/useScrollAnimation.js';
import { getPlanById } from '../../data/subscriptionPlans';
import { Notification } from '../../components/pro_calculator/general/Notification.jsx';
import { logger } from '../../utils/logger.js';
import { useMaintenance } from '../../contexts/MaintenanceContext.jsx';

axios.defaults.withCredentials = true;

const inputClassName =
    'w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60';

const primaryButtonClassName =
    'inline-flex w-full items-center justify-center rounded-2xl bg-blue-700 px-4 py-3.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60';

const FieldLabel = ({ htmlFor, children }) => (
    <label htmlFor={htmlFor} className="mb-2 block text-sm font-medium text-slate-700">
        {children}
    </label>
);

const TextInput = ({ className = '', ...props }) => (
    <input className={`${inputClassName} ${className}`.trim()} {...props} />
);

const MessageBanner = ({ tone = 'error', children }) => {
    const toneClasses = {
        error: 'border-red-200 bg-red-50 text-red-700',
        success: 'border-emerald-200 bg-emerald-50 text-emerald-700',
        info: 'border-blue-200 bg-blue-50 text-blue-700',
    };

    return (
        <div className={`rounded-2xl border px-4 py-3 text-sm ${toneClasses[tone] || toneClasses.info}`}>
            {children}
        </div>
    );
};

const FormShell = ({ eyebrow, title, description, children, footer }) => (
    <div className="rounded-[30px] border border-slate-200 bg-white p-6 shadow-xl shadow-slate-200/60 sm:p-8">
        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-blue-700">{eyebrow}</p>
        <h2 className="mt-4 text-xl font-bold tracking-tight text-slate-900">{title}</h2>
        <p className="mt-3 max-w-lg text-sm leading-6 text-slate-600">{description}</p>
        <div className="mt-8">{children}</div>
        {footer ? <div className="mt-8 border-t border-slate-100 pt-6">{footer}</div> : null}
    </div>
);

function LoginPortal() {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);

    const [newUserUsername, setNewUserUsername] = useState('');
    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserPassword, setNewUserPassword] = useState('');
    const [newUserConfirmPassword, setNewUserConfirmPassword] = useState('');
    const [newUserFirstName, setNewUserFirstName] = useState('');
    const [newUserLastName, setNewUserLastName] = useState('');
    const [newUserCompanyName, setNewUserCompanyName] = useState('');
    const [newUserPhoneNumber, setNewUserPhoneNumber] = useState('');
    const [newUserTermsAndConditions, setNewUserTermsAndConditions] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [termsContent, setTermsContent] = useState('');

    const [showLoginPassword, setShowLoginPassword] = useState(false);
    const [showRegisterPassword, setShowRegisterPassword] = useState(false);
    const [showRegisterConfirmPassword, setShowRegisterConfirmPassword] = useState(false);

    const [showNotification, setShowNotification] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState('');

    const [error, setError] = useState('');
    const [status, setStatus] = useState('');
    const [loginState, setLoginState] = useState('login');
    const [email, setEmail] = useState('');
    const [redirectParams, setRedirectParams] = useState({ redirectTo: null, intent: null, planId: null });

    const [freeTrialToken, setFreeTrialToken] = useState('');
    const apiCallInProgress = useRef(false);
    const tokenValidationComplete = useRef(false);

    const [loading, setLoading] = useState(false);
    const [tokenValidated, setTokenValidated] = useState(false);
    const [formSubmitting, setFormSubmitting] = useState(false);

    const [usernameStatus, setUsernameStatus] = useState({ checking: false, available: null, message: '' });
    const [turnstileToken, setTurnstileToken] = useState('');
    const [turnstileResetKey, setTurnstileResetKey] = useState(0);
    const turnstileRef = useRef(null);
    const errorTimeoutRef = useRef(null);

    const navigate = useNavigate();
    const location = useLocation();
    const { login, isLoading } = useAuth();
    const { maintenanceMode, message: maintenanceMessage, until: maintenanceUntil } = useMaintenance();

    const [formRef, isFormVisible] = useScrollAnimation({ threshold: 0.2 });

    const clearError = () => {
        setError('');
        if (errorTimeoutRef.current) {
            clearTimeout(errorTimeoutRef.current);
            errorTimeoutRef.current = null;
        }
    };

    const validateFreeTrialToken = async (token) => {
        if (apiCallInProgress.current || tokenValidationComplete.current || !token) {
            return;
        }

        apiCallInProgress.current = true;

        try {
            setLoading(true);
            const response = await axios.post(API_ENDPOINTS.tokenValidationACT, { token });

            logger.log('Response:', response.data);
            if (response.data.email) {
                setTokenValidated(true);
                setNewUserEmail(response.data.email);
                tokenValidationComplete.current = true;
                logger.log('Email filled', response.data.email);
            } else {
                setError('This invitation link is invalid or has expired.');
            }
        } catch (err) {
            setError('Unable to validate your invitation. Please try again later.');
            logger.error('Token validation error:', err);
        } finally {
            setLoading(false);
            apiCallInProgress.current = false;
        }
    };

    useEffect(() => {
        if (tokenValidationComplete.current) {
            return;
        }

        logger.log('Checking for free trial token');
        const queryParams = new URLSearchParams(location.search);
        const token = queryParams.get('token');
        logger.log('Token:', token);

        if (token && !apiCallInProgress.current) {
            setLoginState('register');
            setFreeTrialToken(token);
            validateFreeTrialToken(token);
        }
    }, [location]);

    useEffect(() => {
        if (error) {
            if (errorTimeoutRef.current) {
                clearTimeout(errorTimeoutRef.current);
            }

            errorTimeoutRef.current = setTimeout(() => {
                setError('');
                errorTimeoutRef.current = null;
            }, 15000);
        }

        return () => {
            if (errorTimeoutRef.current) {
                clearTimeout(errorTimeoutRef.current);
            }
        };
    }, [error]);

    useEffect(() => {
        if (showTermsModal) {
            fetch('/terms-and-conditions.html')
                .then((response) => response.text())
                .then((html) => {
                    const sanitizedHtml = DOMPurify.sanitize(`<div>${html}</div>`);
                    setTermsContent(sanitizedHtml);
                    logger.log('Fetched and sanitized terms and conditions');
                })
                .catch((fetchError) => {
                    logger.error('Error fetching terms', fetchError);
                });
        }
    }, [showTermsModal]);

    useEffect(() => {
        const handleUserInteraction = () => {
            if (error) {
                clearError();
            }
        };

        document.addEventListener('click', handleUserInteraction);
        document.addEventListener('keydown', handleUserInteraction);

        return () => {
            document.removeEventListener('click', handleUserInteraction);
            document.removeEventListener('keydown', handleUserInteraction);
        };
    }, [error]);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const mode = queryParams.get('mode');
        const redirectFlag = queryParams.get('redirect');
        const redirectTo = queryParams.get('redirectTo');
        const intent = queryParams.get('intent');
        const planId = queryParams.get('planId');
        const prefill = queryParams.get('prefill');

        if (mode === 'register') {
            setLoginState('register');
        }

        setRedirectParams({
            redirectTo,
            intent,
            planId,
        });

        if (prefill) {
            setUsername(prefill);
            setNewUserEmail(prefill);
            setLoginState('login');
        }

        if (redirectTo || redirectFlag === 'true') {
            const planName = planId ? getPlanById(planId)?.name : null;
            const message =
                intent === 'subscribe'
                    ? `Sign in to continue with ${planName || 'your selected plan'}.`
                    : 'Please login to continue.';

            setShowNotification(true);
            setNotificationMessage(message);
        }
    }, [location.search]);

    const requireTurnstileToken = async () => {
        if (!TURNSTILE_SITE_KEY) {
            throw new Error('Please complete the verification check.');
        }
        const token = turnstileRef.current?.getFreshToken
            ? await turnstileRef.current.getFreshToken()
            : turnstileToken;
        if (!token) {
            throw new Error('Please complete the verification check.');
        }
        return token;
    };

    const resetTurnstile = () => {
        setTurnstileToken('');
        setTurnstileResetKey((value) => value + 1);
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        if (maintenanceMode) {
            const untilSuffix = maintenanceUntil ? ` (expected recovery: ${new Date(maintenanceUntil).toLocaleString()})` : '';
            setError((maintenanceMessage || 'Scheduled maintenance in progress.') + untilSuffix);
            return;
        }
        setFormSubmitting(true);
        setStatus('');
        logger.log('Login attempt initiated for username:', username);
        logger.log('Full login URL:', API_ENDPOINTS.login);

        try {
            const cfTurnstileResponse = await requireTurnstileToken();
            const response = await axios.post(API_ENDPOINTS.login, {
                username,
                password,
                'cf-turnstile-response': cfTurnstileResponse,
            });

            const token = response.data.token;
            logger.log('Login successful, received token:', response.data.token ? 'Token exists' : 'No token received');
            localStorage.setItem('authToken', response.data.token);

            const userResponse = await axios.get(API_ENDPOINTS.user, {
                headers: {
                    Authorization: `Token ${token}`,
                },
            });

            await login(
                {
                    token,
                    user: userResponse.data,
                },
                rememberMe
            );

            setError('');

            const safeRedirectPath =
                redirectParams.redirectTo && redirectParams.redirectTo.startsWith('/')
                    ? redirectParams.redirectTo
                    : null;

            const subscriptionStatus = userResponse.data.subscription_status || response.data.subscription_status;
            const defaultDestination = subscriptionStatus === 'active' ? '/home' : '/solutions';

            navigate(safeRedirectPath || defaultDestination);
        } catch (loginError) {
            logger.error('Login failed:', loginError.response?.data || loginError.message);
            const responseStatus = loginError?.response?.status;
            const responseData = loginError?.response?.data;
            const isMaintenance =
                loginError?.isMaintenanceMode ||
                (responseStatus === 503 && Boolean(responseData?.maintenance));

            if (isMaintenance) {
                const msg = responseData?.message || maintenanceMessage || 'Scheduled maintenance in progress.';
                const until = responseData?.until || maintenanceUntil || '';
                const untilSuffix = until ? ` (expected recovery: ${new Date(until).toLocaleString()})` : '';
                setError(`${msg}${untilSuffix}`);
            } else if (!loginError.response && loginError.message) {
                setError(loginError.message);
            } else if (responseData?.error && /verification/i.test(responseData.error)) {
                setError(responseData.error);
            } else {
                setError('Invalid username or password');
            }
            resetTurnstile();
        } finally {
            setFormSubmitting(false);
        }
    };

    const validatePassword = async (currentPassword, confirmPassword) => {
        if (!currentPassword || currentPassword.length < 8) {
            return 'Password must be at least 8 characters long';
        }
        if (!currentPassword.match(/[A-Z]/)) {
            return 'Password must contain at least 1 uppercase letter';
        }
        if (!currentPassword.match(/[a-z]/)) {
            return 'Password must contain at least 1 lowercase letter';
        }
        if (!currentPassword.match(/[0-9]/)) {
            return 'Password must contain at least 1 number';
        }
        if (currentPassword !== confirmPassword) {
            return 'Passwords do not match';
        }
        return null;
    };

    const checkUsernameAvailability = async (nextUsername) => {
        if (!nextUsername || nextUsername.length < 3) {
            setUsernameStatus({ checking: false, available: false, message: 'Username must be at least 3 characters long' });
            return;
        }
        if (!nextUsername || nextUsername.length > 25) {
            setUsernameStatus({ checking: false, available: false, message: 'Username must be less than 25 characters long' });
            return;
        }

        setUsernameStatus({ checking: true, available: null, message: 'Checking username...' });

        try {
            const response = await axios.post(API_ENDPOINTS.isUsernameAvailable, {
                username: nextUsername,
            });

            const isAvailable = response.data.available?.username ?? response.data.all_available;
            const isTaken = response.data.taken?.username ?? false;

            if (isAvailable === true && isTaken === false) {
                setUsernameStatus({
                    checking: false,
                    available: true,
                    message: 'Username is available',
                });
            } else if (isTaken === true) {
                setUsernameStatus({
                    checking: false,
                    available: false,
                    message: 'Username is already taken',
                });
            } else {
                logger.warn('Unexpected response format:', response.data);
                setUsernameStatus({
                    checking: false,
                    available: null,
                    message: 'Could not verify username',
                });
            }
        } catch (usernameError) {
            logger.error('Username check error:', usernameError);
            const errorMsg =
                usernameError.response?.data?.message ||
                usernameError.response?.data?.error ||
                'Could not verify username';

            setUsernameStatus({
                checking: false,
                available: null,
                message: errorMsg,
            });
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setFormSubmitting(true);
        setStatus('');
        logger.log('Register attempt initiated for username:', newUserUsername);

        try {
            const passwordError = await validatePassword(newUserPassword, newUserConfirmPassword);
            if (passwordError) {
                setError(passwordError);
                return;
            }

            if (!newUserUsername || newUserUsername.length < 3) {
                setError('Username must be at least 3 characters long');
                return;
            }
            if (!newUserUsername || newUserUsername.length > 25) {
                setError('Username must be less than 25 characters long');
                return;
            }

            if (usernameStatus.available === false) {
                setError('Username is already taken. Please choose a different username.');
                return;
            }

            if (usernameStatus.available === null) {
                const response = await axios.post(API_ENDPOINTS.isUsernameAvailable, {
                    username: newUserUsername,
                });

                const isAvailable = response.data.available?.username ?? response.data.all_available;
                const isTaken = response.data.taken?.username ?? false;

                if (isTaken === true || isAvailable === false) {
                    setError('Username is already taken. Please choose a different username.');
                    setUsernameStatus({
                        checking: false,
                        available: false,
                        message: 'Username is already taken',
                    });
                    return;
                }

                setUsernameStatus({
                    checking: false,
                    available: true,
                    message: 'Username is available',
                });
            }

            const cfTurnstileResponse = await requireTurnstileToken();
            const response = await axios.post(API_ENDPOINTS.register, {
                username: newUserUsername,
                email: newUserEmail,
                password: newUserPassword,
                confirm_password: newUserConfirmPassword,
                first_name: newUserFirstName,
                last_name: newUserLastName,
                company_name: newUserCompanyName,
                phone_number: newUserPhoneNumber,
                token: freeTrialToken,
                has_token: tokenValidated,
                'cf-turnstile-response': cfTurnstileResponse,
            });

            logger.log('Registration successful, received token:', response.data.token ? 'Token exists' : 'No token received');
            setError('');

            if (tokenValidated) {
                localStorage.setItem('authToken', response.data.token);
                const safeRedirectPath =
                    redirectParams.redirectTo && redirectParams.redirectTo.startsWith('/')
                        ? redirectParams.redirectTo
                        : null;
                navigate(safeRedirectPath || '/home');
            } else {
                setStatus('Account created. Please verify your email before entering product modules.');
                setLoginState('register-success');
            }
        } catch (registerError) {
            logger.error('Registration failed:', registerError.response?.data || registerError.message);

            if (!registerError.response && registerError.message) {
                setError(registerError.message);
            } else if (registerError.response?.data?.error) {
                setError(registerError.response.data.error);
            } else {
                setError('Registration failed. Please try again later.');
            }
            resetTurnstile();
        } finally {
            setFormSubmitting(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setFormSubmitting(true);
        setError('');
        logger.log('Forgot password attempt initiated for email:', email);

        try {
            const cfTurnstileResponse = await requireTurnstileToken();
            await axios.post(API_ENDPOINTS.forgotPassword, {
                email,
                'cf-turnstile-response': cfTurnstileResponse,
            });
            logger.log('Password reset email sent successfully');
            setStatus('Password reset email sent successfully. Please check your email for a reset link.');
            resetTurnstile();
        } catch (forgotPasswordError) {
            logger.error('Password reset failed:', forgotPasswordError.response?.data || forgotPasswordError.message);
            if (!forgotPasswordError.response && forgotPasswordError.message) {
                setError(forgotPasswordError.message);
            } else if (forgotPasswordError.response?.data?.error) {
                setError(forgotPasswordError.response.data.error);
            } else {
                setError('Failed to send password reset email');
            }
            resetTurnstile();
        } finally {
            setFormSubmitting(false);
        }
    };

    const LoadingSpinner = () => (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/25 backdrop-blur-sm">
            <div className="flex flex-col items-center rounded-3xl border border-slate-200 bg-white px-8 py-7 shadow-xl">
                <div className="h-12 w-12 animate-spin rounded-full border-4 border-blue-100 border-t-blue-700" />
                <p className="mt-4 text-sm font-medium text-slate-600">Loading...</p>
            </div>
        </div>
    );

    const renderPasswordField = ({ id, label, value, onChange, visible, onToggle, autoComplete, disabled }) => (
        <div>
            <FieldLabel htmlFor={id}>{label}</FieldLabel>
            <div className="relative">
                <TextInput
                    id={id}
                    type={visible ? 'text' : 'password'}
                    value={value}
                    onChange={onChange}
                    required
                    disabled={Boolean(disabled) || formSubmitting}
                    autoComplete={autoComplete}
                    className="pr-12"
                />
                <button
                    type="button"
                    onClick={onToggle}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-600"
                    disabled={Boolean(disabled) || formSubmitting}
                >
                    {visible ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                </button>
            </div>
        </div>
    );

    const renderLoginForm = () => {
        switch (loginState) {
            case 'login':
                return (
                    <FormShell
                        eyebrow="Sign In"
                        title="Welcome back"
                        // description="Sign in to your account"
                        footer={
                            <div className="flex flex-col gap-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
                                <span>Don&apos;t have an account yet?</span>
                                <button
                                    type="button"
                                    className="text-left font-semibold text-blue-700 transition hover:text-blue-800"
                                    onClick={() => setLoginState('register')}
                                    disabled={formSubmitting}
                                >
                                    Create an account
                                </button>
                            </div>
                        }
                    >
                        <form onSubmit={handleLogin} method="post" action="/api/login" className="space-y-5">
                            {error ? <MessageBanner tone="error">{error}</MessageBanner> : null}

                            <div>
                                <FieldLabel htmlFor="login-username">Email or Username</FieldLabel>
                                <TextInput
                                    id="login-username"
                                    name="username"
                                    type="text"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    required
                                    disabled={formSubmitting || maintenanceMode}
                                    autoComplete="username"
                                    placeholder="Enter your email or username"
                                />
                            </div>

                            {renderPasswordField({
                                id: 'login-password',
                                label: 'Password',
                                value: password,
                                onChange: (e) => setPassword(e.target.value),
                                visible: showLoginPassword,
                                onToggle: () => setShowLoginPassword(!showLoginPassword),
                                autoComplete: 'current-password',
                                disabled: maintenanceMode,
                            })}

                            <div className="flex items-center justify-between gap-4">
                                <label className="inline-flex items-center gap-3 text-sm text-slate-600">
                                    <input
                                        type="checkbox"
                                        checked={rememberMe}
                                        onChange={(e) => setRememberMe(e.target.checked)}
                                        className="h-4 w-4 rounded border-slate-300 text-blue-700 focus:ring-blue-500"
                                        disabled={formSubmitting || maintenanceMode}
                                    />
                                    Keep me logged in
                                </label>
                                <button
                                    type="button"
                                    className="text-sm font-medium text-blue-700 transition hover:text-blue-800"
                                    onClick={() => setLoginState('forgot')}
                                    disabled={formSubmitting || maintenanceMode}
                                >
                                    Forgot password?
                                </button>
                            </div>

                            <button
                                type="submit"
                                className={primaryButtonClassName}
                                disabled={formSubmitting || maintenanceMode}
                            >
                                {formSubmitting ? (
                                    <>
                                        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                                        Logging in...
                                    </>
                                ) : (
                                    maintenanceMode
                                        ? 'Maintenance in progress'
                                        : 'Login'
                                )}
                            </button>

                            <TurnstileWidget
                                ref={turnstileRef}
                                siteKey={TURNSTILE_SITE_KEY}
                                action="login"
                                resetKey={turnstileResetKey}
                                onTokenChange={setTurnstileToken}
                            />
                        </form>
                    </FormShell>
                );

            case 'register':
                return (
                    <FormShell
                        eyebrow="Create Account"
                        title={tokenValidated ? 'Complete your invitation' : 'Let’s get you started'}
                        description={
                            tokenValidated
                                ? 'Your invitation has been verified. Finish the form below to get started.'
                                : 'Keep it simple: create your AmpTrans account and continue into the product.'
                        }
                        footer={
                            <div className="flex flex-col gap-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
                                <span>Already have an account?</span>
                                <button
                                    type="button"
                                    className="text-left font-semibold text-blue-700 transition hover:text-blue-800"
                                    onClick={() => setLoginState('login')}
                                    disabled={formSubmitting}
                                >
                                    Back to login
                                </button>
                            </div>
                        }
                    >
                        <form onSubmit={handleRegister} method="post" action="/api/register" className="space-y-4">
                            {error ? <MessageBanner tone="error">{error}</MessageBanner> : null}

                            <div className="grid gap-4 sm:grid-cols-2">
                                <div>
                                    <FieldLabel htmlFor="register-first-name">First Name *</FieldLabel>
                                    <TextInput
                                        id="register-first-name"
                                        name="first-name"
                                        type="text"
                                        value={newUserFirstName}
                                        onChange={(e) => setNewUserFirstName(e.target.value)}
                                        required
                                        disabled={formSubmitting}
                                        autoComplete="given-name"
                                    />
                                </div>
                                <div>
                                    <FieldLabel htmlFor="register-last-name">Last Name *</FieldLabel>
                                    <TextInput
                                        id="register-last-name"
                                        name="last-name"
                                        type="text"
                                        value={newUserLastName}
                                        onChange={(e) => setNewUserLastName(e.target.value)}
                                        required
                                        disabled={formSubmitting}
                                        autoComplete="family-name"
                                    />
                                </div>
                            </div>

                            <div className="grid gap-4 sm:grid-cols-2">
                                <div>
                                    <FieldLabel htmlFor="register-username">Username *</FieldLabel>
                                    <div className="relative">
                                        <TextInput
                                            id="register-username"
                                            name="username"
                                            type="text"
                                            value={newUserUsername || ''}
                                            onChange={(e) => {
                                                setNewUserUsername(e.target.value);
                                                if (usernameStatus.available !== null) {
                                                    setUsernameStatus({ checking: false, available: null, message: '' });
                                                }
                                            }}
                                            onBlur={(e) => checkUsernameAvailability(e.target.value)}
                                            required
                                            disabled={formSubmitting}
                                            autoComplete="username"
                                            className={
                                                usernameStatus.available === true
                                                    ? 'border-emerald-400 focus:border-emerald-500 focus:ring-emerald-100'
                                                    : usernameStatus.available === false
                                                        ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
                                                        : ''
                                            }
                                        />
                                        {usernameStatus.checking ? (
                                            <div className="absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin rounded-full border-2 border-blue-200 border-t-blue-700" />
                                        ) : null}
                                    </div>
                                    {usernameStatus.message ? (
                                        <p
                                            className={`mt-2 text-xs ${
                                                usernameStatus.available === true
                                                    ? 'text-emerald-600'
                                                    : usernameStatus.available === false
                                                        ? 'text-red-600'
                                                        : 'text-slate-500'
                                            }`}
                                        >
                                            {usernameStatus.message}
                                        </p>
                                    ) : null}
                                </div>
                                <div>
                                    <FieldLabel htmlFor="register-email">Email *</FieldLabel>
                                    {tokenValidated ? (
                                        <div className={`${inputClassName} flex items-center bg-slate-100 text-slate-600`}>
                                            {newUserEmail}
                                        </div>
                                    ) : (
                                        <TextInput
                                            id="register-email"
                                            name="email"
                                            type="email"
                                            value={newUserEmail}
                                            onChange={(e) => setNewUserEmail(e.target.value)}
                                            required
                                            disabled={formSubmitting}
                                            autoComplete="email"
                                        />
                                    )}
                                </div>
                            </div>

                            <div className="grid gap-4 sm:grid-cols-2">
                                {renderPasswordField({
                                    id: 'register-password',
                                    label: 'Password *',
                                    value: newUserPassword,
                                    onChange: (e) => setNewUserPassword(e.target.value),
                                    visible: showRegisterPassword,
                                    onToggle: () => setShowRegisterPassword(!showRegisterPassword),
                                    autoComplete: 'new-password',
                                })}

                                {renderPasswordField({
                                    id: 'register-confirm-password',
                                    label: 'Confirm Password *',
                                    value: newUserConfirmPassword,
                                    onChange: (e) => setNewUserConfirmPassword(e.target.value),
                                    visible: showRegisterConfirmPassword,
                                    onToggle: () => setShowRegisterConfirmPassword(!showRegisterConfirmPassword),
                                    autoComplete: 'new-password',
                                })}
                            </div>

                            <p className="text-xs leading-5 text-slate-500">
                                Password must contain at least 8 characters, 1 uppercase letter, 1 lowercase letter, and 1 number.
                            </p>

                            <div className="grid gap-4 sm:grid-cols-2">
                                <div>
                                    <FieldLabel htmlFor="company-name">Company Name</FieldLabel>
                                    <TextInput
                                        id="company-name"
                                        type="text"
                                        value={newUserCompanyName}
                                        onChange={(e) => setNewUserCompanyName(e.target.value)}
                                        disabled={formSubmitting}
                                    />
                                </div>
                                <div>
                                    <FieldLabel htmlFor="phone-number">Phone Number</FieldLabel>
                                    <TextInput
                                        id="phone-number"
                                        type="text"
                                        value={newUserPhoneNumber}
                                        onChange={(e) => setNewUserPhoneNumber(e.target.value)}
                                        disabled={formSubmitting}
                                    />
                                </div>
                            </div>

                            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                                <label htmlFor="terms-and-conditions" className="flex items-start gap-3 text-sm text-slate-600">
                                    <input
                                        type="checkbox"
                                        id="terms-and-conditions"
                                        checked={newUserTermsAndConditions}
                                        onChange={(e) => setNewUserTermsAndConditions(e.target.checked)}
                                        className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-700 focus:ring-blue-500"
                                        disabled={formSubmitting}
                                    />
                                    <span>
                                        I agree to the{' '}
                                        <button
                                            type="button"
                                            onClick={() => setShowTermsModal(true)}
                                            className="font-medium text-blue-700 underline underline-offset-2 transition hover:text-blue-800"
                                        >
                                            Terms and Conditions
                                        </button>
                                        .
                                    </span>
                                </label>
                            </div>

                            <p className="text-xs text-slate-500">Fields marked with * are required.</p>

                            <button
                                type="submit"
                                className={primaryButtonClassName}
                                disabled={!newUserTermsAndConditions || formSubmitting}
                            >
                                {formSubmitting ? (
                                    <>
                                        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                                        Creating account...
                                    </>
                                ) : (
                                    'Create Account'
                                )}
                            </button>

                            <TurnstileWidget
                                ref={turnstileRef}
                                siteKey={TURNSTILE_SITE_KEY}
                                action="register"
                                resetKey={turnstileResetKey}
                                onTokenChange={setTurnstileToken}
                            />
                        </form>

                        {showTermsModal ? (
                            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
                                <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-2xl">
                                    <div className="flex items-center justify-between border-b border-slate-200 px-6 py-5">
                                        <div>
                                            <h3 className="text-lg font-semibold text-slate-900">Terms and Conditions</h3>
                                            <p className="mt-1 text-sm text-slate-500">Review the terms before continuing.</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setShowTermsModal(false)}
                                            className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                                        >
                                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>

                                    <div className="prose prose-sm max-w-none flex-1 overflow-y-auto px-6 py-6 text-slate-700">
                                        <div dangerouslySetInnerHTML={{ __html: termsContent }} />
                                    </div>

                                    <div className="border-t border-slate-200 bg-slate-50 px-6 py-4">
                                        <button
                                            type="button"
                                            onClick={() => setShowTermsModal(false)}
                                            className="rounded-xl bg-blue-700 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-blue-800"
                                        >
                                            Close
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </FormShell>
                );

            case 'register-success':
                return (
                    <FormShell
                        eyebrow="Success"
                        title="Check your inbox"
                        description="Registration was successful. Follow the verification link in your email to finish setting up your account."
                        footer={
                            <button
                                type="button"
                                className="font-semibold text-blue-700 transition hover:text-blue-800"
                                onClick={() => setLoginState('login')}
                            >
                                Back to login
                            </button>
                        }
                    >
                        <MessageBanner tone="success">
                            Registration successful. Once your email is verified, you can sign in normally.
                        </MessageBanner>
                    </FormShell>
                );

            case 'forgot':
                return (
                    <FormShell
                        eyebrow="Reset Password"
                        title="Get a reset link"
                        description="Enter the email associated with your account and we&apos;ll send you a secure password reset link."
                        footer={
                            <button
                                type="button"
                                className="font-semibold text-blue-700 transition hover:text-blue-800"
                                onClick={() => setLoginState('login')}
                                disabled={formSubmitting}
                            >
                                Back to login
                            </button>
                        }
                    >
                        <form onSubmit={handleForgotPassword} className="space-y-5">
                            {error ? <MessageBanner tone="error">{error}</MessageBanner> : null}
                            {status ? <MessageBanner tone="success">{status}</MessageBanner> : null}

                            <div>
                                <FieldLabel htmlFor="email">Email</FieldLabel>
                                <TextInput
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    disabled={formSubmitting}
                                    autoComplete="email"
                                    placeholder="name@company.com"
                                />
                            </div>

                            <button type="submit" className={primaryButtonClassName} disabled={formSubmitting}>
                                {formSubmitting ? (
                                    <>
                                        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                                        Sending...
                                    </>
                                ) : (
                                    'Send reset link'
                                )}
                            </button>

                            <TurnstileWidget
                                ref={turnstileRef}
                                siteKey={TURNSTILE_SITE_KEY}
                                action="forgot-password"
                                resetKey={turnstileResetKey}
                                onTokenChange={setTurnstileToken}
                            />
                        </form>
                    </FormShell>
                );

            default:
                return null;
        }
    };

    if (isLoading || loading) {
        return <LoadingSpinner />;
    }

    return (
        <div className="relative min-h-screen overflow-hidden bg-slate-50">
            <div className="absolute inset-0">
                <div className="absolute left-0 top-0 h-80 w-80 rounded-full bg-blue-100/70 blur-3xl" />
                <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-cyan-100/60 blur-3xl" />
                <div
                    className="absolute inset-0 opacity-[0.03]"
                    style={{
                        backgroundImage:
                            'linear-gradient(rgba(15,23,42,0.9) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.9) 1px, transparent 1px)',
                        backgroundSize: '40px 40px',
                    }}
                />
            </div>

            <div className="relative z-10 px-4 pt-6 sm:px-6 lg:px-8">
                <Link to="/" className="inline-flex items-center">
                    <img src="/img/logo.png" alt="AmpTrans Logo" className="h-11 w-auto sm:h-12" />
                </Link>
            </div>

            {showNotification ? (
                <Notification
                    message={notificationMessage}
                    type="info"
                    visible={showNotification}
                    onClose={() => setShowNotification(false)}
                />
            ) : null}

            <div className="relative mx-auto flex min-h-[calc(100vh-5rem)] max-w-7xl items-center justify-center px-4 py-10 sm:px-6 lg:px-8">
                <div
                    ref={formRef}
                    className={`w-full ${loginState === 'register' ? 'max-w-2xl' : 'max-w-xl'} transition-all duration-1000 ease-out ${
                        isFormVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                    }`}
                >
                    {renderLoginForm()}
                </div>
            </div>
        </div>
    );
}

export default LoginPortal;